import React, { createContext } from "react";
import { currentOpenOverlay } from "Shared/Overlay";
import { isBrowser } from "Shared/device-type";
const listeners = {};
// This component is needed to bind events to the most outer
// component but still use Reacts event system. If we were to
// bind directly to `document` you won't be able to stop the
// propgation to those handlers from inside React, since React
// only binds one handler to `document`.
export const RootContext = createContext({
    rootElementRef: null,
});
export default class Root extends React.Component {
    componentDidMount() {
        this.forceUpdate();
    }
    render() {
        return (React.createElement(RootContext.Provider, { value: { rootElementRef: this.elementRef } },
            React.createElement("div", { ref: (el) => (this.elementRef = el), className: this.props.className, onClick: triggerEvent, onKeyDown: triggerEvent }, this.props.children)));
    }
}
export function on(event, callback) {
    if (!isBrowser()) {
        console.warn("Root.on should not be called on the server");
        return;
    }
    const events = event.split(" ");
    events.forEach((eventName) => {
        if (!listeners[eventName]) {
            listeners[eventName] = [];
        }
        listeners[eventName].push(callback);
        handleWindowSubscription(eventName);
    });
    // We return a function here that you can call to unscribe to the event.
    // This instead of exporting an `off` function where you have to pass
    // the same function you passed to `on` which makes it impossible to
    // pass an inline function to `on`.
    return () => events.forEach((eventName) => {
        listeners[eventName] = listeners[eventName].filter((listener) => listener !== callback);
        handleWindowSubscription(eventName);
    });
}
export function scrollPosition(newPosition = undefined) {
    if (newPosition !== undefined) {
        document.body.scrollTop = newPosition;
        document.documentElement.scrollTop = newPosition;
        if (currentOpenOverlay) {
            // Because of iOS, we make the body element position: fixed while an overlay
            // is up so we need to notify the current overlay that we've updated the scroll
            currentOpenOverlay.setCurrentScrollPosition(newPosition);
        }
    }
    return Math.max(document.body.scrollTop, document.documentElement.scrollTop);
}
export function scrollTo(scrollTo, duration) {
    const startTime = Date.now();
    const endTime = startTime + duration;
    const startTop = scrollPosition();
    const distance = scrollTo - startTop;
    const smoothStep = (start, end, point) => {
        if (point <= start) {
            return 0;
        }
        if (point >= end) {
            return 1;
        }
        const x = (point - start) / (end - start);
        return x * x * (3 - 2 * x);
    };
    const onScroll = (e) => e.stopPropagation();
    return new Promise((resolve, reject) => {
        let previousTop = scrollPosition();
        const scrollFrame = () => {
            if (scrollPosition() !== previousTop) {
                return resolve();
            }
            const now = Date.now();
            const point = smoothStep(startTime, endTime, now);
            const frameTop = Math.round(startTop + distance * point);
            scrollPosition(frameTop);
            if (now >= endTime) {
                return resolve();
            }
            if (scrollPosition() === previousTop && scrollPosition() !== frameTop) {
                return resolve();
            }
            previousTop = scrollPosition();
            requestAnimationFrame(scrollFrame);
        };
        // We don't let anyone else notice the scroll events because
        // we want a smooth animation
        window.addEventListener("scroll", onScroll, true);
        requestAnimationFrame(scrollFrame);
    }).then(() => {
        window.removeEventListener("scroll", onScroll, true);
    });
}
export function one(event, callback) {
    const unsubscribe = on(event, (e) => {
        callback(e);
        unsubscribe();
    });
}
function hasSubscribers(eventName) {
    return listeners[eventName] && listeners[eventName].length > 0;
}
export function triggerEvent(e) {
    if (!hasSubscribers(e.type)) {
        return;
    }
    listeners[e.type].forEach((listener) => {
        listener(e);
    });
}
const throttling = [];
// Some events than can fire at a high rate and execute computationally
// expensive operations such as DOM modifications. Those events should
// be throttled, such as `scroll` and `resize`.
function throttleEvent(e) {
    if (!hasSubscribers(e.type) || throttling.indexOf(e.type) !== -1) {
        return;
    }
    throttling.push(e.type);
    window.requestAnimationFrame(() => {
        triggerEvent(e);
        throttling.splice(throttling.indexOf(e.type), 1);
    });
}
const windowEvents = {
    resize: throttleEvent,
    scroll: throttleEvent,
    // We need `touchmove` on the window because you can't swipe out
    // the drawer/mini cart
    touchmove: throttleEvent,
    touchstart: throttleEvent,
    touchend: throttleEvent,
};
const boundWindowEvents = [];
// Pass `window` events defined in `windowEvents` to this module. Should be
// invoked when `listeners` has been manipulated
function handleWindowSubscription(eventName) {
    const windowEvent = windowEvents[eventName];
    if (windowEvent) {
        if (hasSubscribers(eventName) && !boundWindowEvents.includes(eventName)) {
            window.addEventListener(eventName, windowEvent);
            boundWindowEvents.push(eventName);
        }
        else if (!hasSubscribers(eventName) &&
            boundWindowEvents.includes(eventName)) {
            window.removeEventListener(eventName, windowEvent);
            boundWindowEvents.splice(boundWindowEvents.indexOf(eventName), 1);
        }
    }
}
